import { API_CONFIG } from '../config/api.js'

// Вспомогательная функция для парсинга JSON
function tryParseJSON(str) {
  try {
    return JSON.parse(str)
  } catch (e) {
    return null
  }
}

class ApiService {
  constructor() {
    this.baseURL = API_CONFIG.BASE_URL
    this.timeout = 15000 // 15 секунд
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), this.timeout)

    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        }
      })

      clearTimeout(timeoutId)

      // Статусы 200-299 и 304 считаются успешными
      const isSuccess = (response.status >= 200 && response.status < 300) || response.status === 304

      let data = null
      // Пытаемся получить данные из ответа
      try {
        const contentType = response.headers.get('content-type')
        if (contentType && contentType.includes('application/json')) {
          data = await response.json()
        } else {
          // Если нет JSON, пробуем получить текст или null
          const text = await response.text()
          data = text ? (tryParseJSON(text) || text) : null
        }
      } catch (error) {
        // Если не удалось прочитать ответ, оставляем null
        data = null
      }

      return {
        status: response.status,
        ok: isSuccess,
        data: data
      }
    } catch (error) {
      clearTimeout(timeoutId)
      
      if (error.name === 'AbortError') {
        throw new Error('TIMEOUT')
      }
      
      throw error
    }
  }

  async getUserData(userId) {
    // Получаем данные пользователя по user_id
    return this.request(`/userdata?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async createUser(userData) {
    // Отправляем все доступные данные пользователя
    // userData должен содержать: user_id, username (обязательные), user_firstname, user_lastname (опциональные)
    const body = {}
    
    if (userData.user_id) body.user_id = userData.user_id
    if (userData.username) body.username = userData.username
    if (userData.user_firstname) body.user_firstname = userData.user_firstname
    if (userData.user_lastname) body.user_lastname = userData.user_lastname
    
    return this.request('/user', {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async createPin(userId, pin) {
    // Отправляем user_id и PIN-код для создания
    return this.request('/createpin', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        pin_code: pin
      })
    })
  }

  async isPin(userId) {
    // Проверяем наличие PIN-кода у пользователя
    return this.request(`/is_pin?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async verifyPin(userId, pin) {
    // Проверяем правильность PIN-кода
    return this.request('/check_pin', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        pin_code: pin
      })
    })
  }

  async checkWorker(userId) {
    // Проверяет статус пользователя (является ли он воркером)
    return this.request(`/check_worker?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async checkAdmin(userId) {
    // Проверяет статус пользователя (является ли он админом)
    return this.request(`/check_admin?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getLandings(workerId) {
    // Получаем список лендингов для воркера
    return this.request(`/landings?worker_id=${workerId}`, {
      method: 'GET'
    })
  }

  async createLanding(landingData) {
    // Создаем новый лендинг
    // landingData должен содержать: ref_code, name, worker_id, avatar
    return this.request('/landing', {
      method: 'POST',
      body: JSON.stringify({
        ref_code: landingData.ref_code,
        name: landingData.name,
        worker_id: landingData.worker_id,
        avatar: landingData.avatar || null
      })
    })
  }

  async deleteLanding(refCode) {
    // Удаляем лендинг по ref_code
    return this.request('/landing', {
      method: 'DELETE',
      body: JSON.stringify({
        ref_code: refCode
      })
    })
  }

  async assignWorker(userId, refCode) {
    // Привязываем пользователя к воркеру по реферальному коду
    return this.request('/assign_worker', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        ref_code: refCode
      })
    })
  }

  async checkWorkerAssignment(userId) {
    // Проверяем, привязан ли пользователь к воркеру
    return this.request(`/check_worker_assignment?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getLeads(userId) {
    // Получаем список лидов для пользователя
    return this.request(`/leads?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getWorkers() {
    // Получаем список всех воркеров
    return this.request('/get_workers', {
      method: 'GET'
    })
  }

  async addWorker(username, adminUserId) {
    // Добавляем воркера по username
    return this.request('/add_worker', {
      method: 'POST',
      body: JSON.stringify({
        username: username,
        admin_user_id: adminUserId
      })
    })
  }

  async removeWorker(username, adminUserId) {
    // Удаляем воркера по username
    return this.request('/remove_worker', {
      method: 'POST',
      body: JSON.stringify({
        username: username,
        admin_user_id: adminUserId
      })
    })
  }

  async findTrader(userId) {
    // Получаем информацию о трейдере, которого отслеживает пользователь
    return this.request(`/find_trader?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async stopCopy(userId) {
    // Останавливает копирование сделок трейдера
    return this.request('/stopcopy', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId
      })
    })
  }

  async resumeCopy(userId) {
    // Возобновляет копирование сделок трейдера
    return this.request('/resumecopy', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId
      })
    })
  }

  async addBalance(userId, amount) {
    // Добавляет баланс пользователю
    return this.request('/add-balance', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        amount: amount
      })
    })
  }

  async removeBalance(userId, amount) {
    // Удаляет баланс у пользователя
    return this.request('/remove-balance', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        amount: amount
      })
    })
  }

  async openPosition(userId, coin, direction, amount, time) {
    // Открывает позицию для пользователя
    return this.request('/open-position', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        coin: coin,
        direction: direction,
        amount: amount,
        time: time
      })
    })
  }

  async createPosition(userId, direction, margin, coinId = null, entryPrice = null, leverage = null, createdBy = null) {
    // Создает новую позицию для пользователя
    const body = {
      user_id: userId,
      direction: direction,
      margin: margin
    }

    if (coinId) {
      body.coin_id = coinId
    }

    if (entryPrice !== null && entryPrice !== undefined) {
      body.entry_price = entryPrice
    }

    if (leverage !== null && leverage !== undefined) {
      body.leverage = leverage
    }

    // Кто открыл позицию (id менеджера из админки) — для атрибуции «кто открыл»
    if (createdBy !== null && createdBy !== undefined && createdBy !== '') {
      body.created_by = createdBy
    }

    return this.request('/create-position', {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async closePosition(userId, positionId, coin, entryPrice, exitPrice, pnl, dateOpened = null, dateClosed = null) {
    // Закрывает позицию пользователя
    const body = {
      user_id: userId,
      position_id: positionId,
      coin: coin,
      entry_price: entryPrice,
      exit_price: exitPrice,
      pnl: pnl
    }
    
    if (dateOpened) {
      body.date_opened = dateOpened
    }
    
    if (dateClosed) {
      body.date_closed = dateClosed
    }
    
    return this.request('/close-position', {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async changePosition(userId, positionId, data) {
    // Изменяет открытую позицию пользователя (из админки)
    return this.request('/change-position', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        position_id: positionId,
        ...data
      })
    })
  }

  async addMessage(userId, message) {
    // Добавляет сообщение пользователю
    return this.request('/add-message', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        message: message
      })
    })
  }

  async removeMessage(userId, messageId) {
    // Удаляет сообщение пользователя
    const requestBody = {
      user_id: userId,
      message_id: messageId
    }
    
    console.log('[apiService] removeMessage called with:', requestBody)
    
    const response = await this.request('/remove-message', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    })
    
    console.log('[apiService] removeMessage response:', response)
    
    return response
  }

  async addExp(userId, exp) {
    // Добавляет опыт пользователю
    return this.request('/add-exp', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        exp: exp
      })
    })
  }

  async removeExp(userId, exp) {
    // Удаляет опыт у пользователя
    return this.request('/remove-exp', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        exp: exp
      })
    })
  }

  async getPositions(userId) {
    // Получает открытые и закрытые позиции пользователя
    return this.request('/get-positions', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId
      })
    })
  }

  async getPnl() {
    // Получает PnL данные (15 значений)
    return this.request('/get-pnl', {
      method: 'GET'
    })
  }

  async sendHeartbeat(userId) {
    // Отправляет сигнал активности пользователя (heartbeat)
    return this.request('/heartbeat', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId
      })
    })
  }

  async getUserStatus(userId) {
    // Получает статус пользователя (online/offline)
    return this.request(`/user-status?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getUsersStatuses(userIds) {
    // Получает статусы нескольких пользователей
    return this.request('/users-statuses', {
      method: 'POST',
      body: JSON.stringify({
        user_ids: userIds
      })
    })
  }

  async setCustomPnl(userId, positionId, customPnlPercent) {
    // Устанавливает кастомный PnL для позиции
    return this.request('/custom-pnl', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        position_id: positionId,
        custom_pnl_percent: customPnlPercent
      })
    })
  }

  async getCustomPnl(userId, positionId) {
    // Получает кастомный PnL для позиции
    return this.request(`/custom-pnl?user_id=${userId}&position_id=${positionId}`, {
      method: 'GET'
    })
  }

  async getUserCustomPnls(userId) {
    // Получает все кастомные PnL для пользователя
    return this.request(`/custom-pnl/user/${userId}`, {
      method: 'GET'
    })
  }

  async getConnectedAddress(userId) {
    // Получает connected address для лида
    return this.request(`/connected-address?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getBalanceData(userId) {
    // Получает детальную информацию о балансе лида
    return this.request(`/balance-data?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async changeBalanceData(userId, balanceData) {
    // Изменяет данные баланса лида (только balance и tradingCredit)
    return this.request('/change-balance-data', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        balance: balanceData.balance,
        tradingCredit: balanceData.tradingCredit
      })
    })
  }

  async removeCustomPnl(userId, positionId) {
    // Удаляет кастомный PnL для позиции
    return this.request('/custom-pnl', {
      method: 'DELETE',
      body: JSON.stringify({
        user_id: userId,
        position_id: positionId
      })
    })
  }

  async sendNotification(userId, message) {
    // Отправляет уведомление о действии лида для воркеров
    return this.request('/notifications', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        message: message
      })
    })
  }

  async toggleAccessBlock(userId, blocked) {
    // Блокирует/разблокирует доступ лида к приложению
    return this.request('/toggle-access-block', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        blocked: blocked
      })
    })
  }

  async toggleTradingBlock(userId, blocked) {
    // Блокирует/разблокирует торговлю лида (открытие/закрытие позиций и ордеров)
    return this.request('/toggle-trading-block', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        blocked: blocked
      })
    })
  }

  async updateClosedPosition(userId, positionId, positionData) {
    // Обновляет данные закрытой позиции
    return this.request('/update-closed-position', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        position_id: positionId,
        ...positionData
      })
    })
  }

  async createWithdrawRequest(userId, withdrawData) {
    // Создает заявку на вывод средств
    return this.request('/withdraw-request', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        ...withdrawData
      })
    })
  }

  async getTransactionHistory(userId) {
    // Получает историю пополнений и выводов
    return this.request(`/transaction-history?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getWithdrawRequests(userId) {
    // Получает все заявки на вывод средств
    return this.request(`/withdraw-requests?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async getDepositRequests(userId) {
    // Получает все заявки на пополнение средств
    return this.request(`/deposit-requests?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async updateWithdrawRequest(userId, requestId, status, reason = null) {
    // Обновляет статус заявки на вывод средств
    const body = {
      user_id: userId,
      request_id: requestId,
      status: status
    }
    if (reason) {
      body.reason = reason
    }
    return this.request('/update-withdraw-request', {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async updateWithdrawRequestNetwork(userId, requestId, network) {
    // Обновляет сеть для заявки на вывод средств
    return this.request('/update-withdraw-network', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        request_id: requestId,
        network: network
      })
    })
  }

  async transferLead(leadUserId, newWorkerUserId, oldWorkerUserId) {
    // Передает управление лидом другому воркеру
    return this.request('/transfer-lead', {
      method: 'POST',
      body: JSON.stringify({
        lead_user_id: leadUserId,
        new_worker_user_id: newWorkerUserId,
        old_worker_user_id: oldWorkerUserId
      })
    })
  }

  async connectAddress(connectAddress, refCode) {
    // Подключает адрес кошелька к воркеру
    return this.request('/connected', {
      method: 'POST',
      body: JSON.stringify({
        connect_address: connectAddress,
        ref_code: refCode
      })
    })
  }

  async updateConnectedAddress(userId, connectedAddress) {
    // Обновляет connected address у лида вручную (админ)
    return this.request('/update-connected-address', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        connected_address: connectedAddress
      })
    })
  }

  async updateLeadStatus(userId, status) {
    // Изменяет статус лида
    return this.request('/update-lead-status', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        status: status
      })
    })
  }

  async updateAllLeadsStatus() {
    // Обновляет все существующие лиды на статус NEW
    return this.request('/update-all-leads-status', {
      method: 'POST'
    })
  }

  async updateCustomTrader(userId, customTraderName, customTraderAvatar) {
    // Обновляет кастомные данные трейдера для лида
    return this.request('/update-custom-trader', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        custom_trader_name: customTraderName,
        custom_trader_avatar: customTraderAvatar
      })
    })
  }

  async checkDeposits(userId) {
    // Проверяет новые депозиты (heartbeat)
    return this.request('/check-deposits', {
      method: 'POST',
      body: JSON.stringify({ user_id: userId })
    })
  }

  async getDepositAddress(userId) {
    // Получает адрес кошелька для депозита
    return this.request(`/connected-address?user_id=${userId}`, {
      method: 'GET'
    })
  }

  async declineDeposit(userId, depositId, reason = null) {
    // Отклоняет депозит (админ)
    const body = {
      user_id: userId,
      deposit_id: depositId
    }
    if (reason) {
      body.reason = reason
    }
    return this.request('/decline-deposit', {
      method: 'POST',
      body: JSON.stringify(body)
    })
  }

  async editWithdrawRequest(userId, requestId, fields) {
    return this.request('/edit-withdraw-request', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        request_id: requestId,
        fields
      })
    })
  }

  async editDepositRequest(userId, depositId, fields) {
    return this.request('/edit-deposit-request', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        deposit_id: depositId,
        fields
      })
    })
  }

  // ===== Самостоятельная торговля (фьючерсы) =====
  async tradeOpen(payload) {
    return this.request('/trade/open', { method: 'POST', body: JSON.stringify(payload) })
  }
  async tradePlaceOrder(payload) {
    return this.request('/trade/order', { method: 'POST', body: JSON.stringify(payload) })
  }
  async tradeCancelOrder(userId, orderId) {
    return this.request('/trade/cancel-order', { method: 'POST', body: JSON.stringify({ user_id: userId, order_id: orderId }) })
  }
  async tradeClose(userId, positionId, exitPrice, displayedPnl) {
    const payload = { user_id: userId, position_id: positionId }
    if (exitPrice != null) payload.exit_price = exitPrice
    if (displayedPnl != null) payload.displayed_pnl = displayedPnl
    return this.request('/trade/close', { method: 'POST', body: JSON.stringify(payload) })
  }
  async tradeUpdateTpSl(userId, positionId, takeProfit, stopLoss) {
    return this.request('/trade/update-tpsl', { method: 'POST', body: JSON.stringify({ user_id: userId, position_id: positionId, take_profit: takeProfit, stop_loss: stopLoss }) })
  }
  async tradeUpdateOrder(userId, orderId, limitPrice, takeProfit, stopLoss, margin) {
    return this.request('/trade/update-order', { method: 'POST', body: JSON.stringify({ user_id: userId, order_id: orderId, limit_price: limitPrice, take_profit: takeProfit, stop_loss: stopLoss, margin: margin }) })
  }
  async coingeckoSearch(query) {
    return this.request(`/coingecko/search?query=${encodeURIComponent(query)}`, { method: 'GET' })
  }
  async tradeGetState(userId) {
    return this.request(`/trade/state?user_id=${userId}`, { method: 'GET' })
  }

  async deleteTransaction(userId, txType, transactionId, adjustBalance = true) {
    // Удаляет транзакцию (депозит или вывод) клиента. По умолчанию откатывает
    // влияние approved-транзакции на баланс. transactionId = id заявки.
    return this.request('/delete-transaction', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        tx_type: txType,
        transaction_id: transactionId,
        adjust_balance: adjustBalance
      })
    })
  }
}

export const apiService = new ApiService()
